import { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_DEBOUNCE_MS = 350;

export const usePaginatedSearch = (
  fetchPage,
  { pageSize = DEFAULT_PAGE_SIZE, debounceMs = DEFAULT_DEBOUNCE_MS, enabled = true } = {}
) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), debounceMs);
    return () => clearTimeout(timer);
  }, [inputValue, debounceMs]);

  useEffect(() => {
    if (!enabled) return;
    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchPage({
          page: 0,
          size: pageSize,
          search: debouncedSearch || undefined,
        });
        if (cancelled || requestId !== requestIdRef.current) return;
        setOptions(Array.isArray(result?.content) ? result.content : []);
        setPage(0);
        setTotalPages(Number(result?.totalPages) || 1);
      } catch {
        if (cancelled || requestId !== requestIdRef.current) return;
        setOptions([]);
        setTotalPages(1);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, pageSize, enabled]);

  const hasMore = page + 1 < totalPages;

  const loadMore = async () => {
    if (!enabled || loading || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    const requestId = ++requestIdRef.current;
    setLoadingMore(true);
    try {
      const result = await fetchPage({
        page: nextPage,
        size: pageSize,
        search: debouncedSearch || undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setOptions((prev) => [...prev, ...(Array.isArray(result?.content) ? result.content : [])]);
      setPage(nextPage);
      setTotalPages(Number(result?.totalPages) || 1);
    } catch {
      // leave current options/page as-is; user can retry by scrolling again
    } finally {
      if (requestId === requestIdRef.current) setLoadingMore(false);
    }
  };

  // Fires loadMore() once the listbox is scrolled within one row's height of the bottom.
  // Spread onto MUI Autocomplete's ListboxProps.
  const listboxProps = useMemo(
    () => ({
      onScroll: (event) => {
        const el = event.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
          loadMore();
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMore]
  );

  return {
    inputValue,
    setInputValue,
    debouncedSearch,
    options,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    listboxProps,
  };
};
